import { type User } from '../../../../contexts/authContext/types/user.type';
import { APPLICATION_STATUS_TYPE } from '../../../../utils/statusConstants/statusConstants';
import { FundingSource } from '../../../Nomination/components/NominationForm/FundingModule/types';
import { HKPFS_NOMINEE_FILTER } from '../../../ScholarshipManagement/constants/fundingStatus';

export const dorpgsToDoFilter = (user: User | null): any => {
  const { userInfo } = user ?? {};
  const { department } = userInfo ?? {};

  return {
    status: {
      in: [
        APPLICATION_STATUS_TYPE.SUPERVISOR_NOMINATED,
        APPLICATION_STATUS_TYPE.UNDER_DORPGS,
      ],
    },

    programmeChoice: {
      department: { eq: department },
    },
  };
};

export const dorpgsOverViewFilter = (user: User | null): any => {
  // includes the delegating mode
  const department = user?.userInfo.department ?? '';

  const filter = {
    and: [
      {
        programmeChoice: {
          department: { eq: department },
        },
      },
      {
        // TODO deal with the display of withdrawn
        status: {
          nin: [
            APPLICATION_STATUS_TYPE.CREATED,
            APPLICATION_STATUS_TYPE.APPLICATION_FEE_PENDING,
          ],
        },
      },
    ],
  };

  return filter;
};

export const drcApplicationListFilter = (user: User | null): any => {
  const { userInfo } = user ?? {};
  const { department } = userInfo ?? {};

  return {
    application: {
      // GS members can view applications from all departments without departmental filters.
      ...(department === 'GS'
        ? null
        : {
            programmeChoice: {
              department: { eq: department },
            },
          }),
    },
  };
};

// remark: must use applicationsPaginated instead of nominationsPaginated
export const nonProposedFilter = (user: User | null): any => {
  const { userInfo } = user ?? {};
  const { department } = userInfo ?? {};

  const filter = {
    or: [
      {
        and: [
          { status: { eq: APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL } },
          {
            programmeChoice: {
              department: { eq: department },
            },
          },
        ],
      },
      {
        and: [
          { status: { eq: APPLICATION_STATUS_TYPE.SUBMITTED } },
          {
            programmeChoice: {
              department: { eq: department },
              proposedChiefSupervisor: {
                eq: null,
              },
            },
          },
        ],
      },
    ],
  };

  return filter;
};

export const drcConditionToMeetNumFilter = (user: User | null): any => {
  const { userInfo } = user ?? {};
  const { department } = userInfo ?? {};

  const filter = {
    and: [
      {
        status: {
          in: [
            APPLICATION_STATUS_TYPE.OFFERED,
            APPLICATION_STATUS_TYPE.OVERDUE,
            APPLICATION_STATUS_TYPE.REGISTERED,
            APPLICATION_STATUS_TYPE.CONDITION_MEET_LAPSED,

            APPLICATION_STATUS_TYPE.REGISTRATION_FEE_PENDING,
            APPLICATION_STATUS_TYPE.REGISTRATION_FEE_SETTLED,
            APPLICATION_STATUS_TYPE.UNDER_REGISTRATION,
          ],
        },
      },
      {
        programmeChoice: {
          department: { eq: department },
        },
      },
      {
        meetConditionCheckModel: {
          isFirmOffer: false,
        },
      },
    ],
  };
  return filter;
};

export const extendRegistrationFeeFilter = (
  user: User | null,
  isDepartmentLevel: boolean,
): any => {
  const { userInfo } = user ?? {};
  const { department } = userInfo ?? {};
  const deptFilter = {
    programmeChoice: {
      department: { eq: department },
    },
  };
  const filter = {
    and: [
      {
        status: {
          in: [
            APPLICATION_STATUS_TYPE.OFFERED,
            APPLICATION_STATUS_TYPE.LAPSED,
            APPLICATION_STATUS_TYPE.OVERDUE,
            APPLICATION_STATUS_TYPE.CONDITION_MEET_LAPSED,
            APPLICATION_STATUS_TYPE.OFFER_PROVISIONING,
            APPLICATION_STATUS_TYPE.FAILED_PROVISIONING,
            APPLICATION_STATUS_TYPE.REGISTRATION_FEE_PENDING,
          ],
        },
      },
      ...(isDepartmentLevel ? [deptFilter] : []),
    ],
  };
  return filter;
};

export const commenceOfStudyFilter = (
  user: User | null,
  isDepartmentLevel: boolean,
): any => {
  const { userInfo } = user ?? {};
  const { department } = userInfo ?? {};
  const deptFilter = {
    programmeChoice: {
      department: { eq: department },
    },
  };
  const filter = {
    and: [
      {
        status: {
          in: [APPLICATION_STATUS_TYPE.REGISTERED],
        },
      },
      ...(isDepartmentLevel ? [deptFilter] : []),
    ],
  };
  return filter;
};

export const dorpgPolishCommentsFilter = {
  nomination: {
    fundings: {
      source: { eq: FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME },
      status: { in: HKPFS_NOMINEE_FILTER },
    },
  },
  status: {
    nin: [
      APPLICATION_STATUS_TYPE.CREATED,
      APPLICATION_STATUS_TYPE.APPLICATION_FEE_PENDING,
      APPLICATION_STATUS_TYPE.SUBMITTED,
    ],
  },
};

export const changeSupervisorFilter = (
  user: User | null,
  isDepartmentLevel: boolean,
): any => {
  const { userInfo } = user ?? {};
  const { department } = userInfo ?? {};

  const deptFilter = {
    programmeChoice: {
      department: { eq: department },
    },
  };
  const filter = {
    and: [
      {
        status: {
          in: [
            APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR,
            APPLICATION_STATUS_TYPE.UNDER_DORPGS,
            APPLICATION_STATUS_TYPE.UNDER_DSRC,
            APPLICATION_STATUS_TYPE.UNDER_HOD_DOS,
            APPLICATION_STATUS_TYPE.UNDER_FRCSB,
            APPLICATION_STATUS_TYPE.UNDER_PANEL,
            APPLICATION_STATUS_TYPE.UNDER_INTERVIEW_PANEL,
          ],
        },
      },
      ...(isDepartmentLevel ? [deptFilter] : []),
    ],
  };
  return filter;
};

export const rejectApplicationFilter = (
  user: User | null,
  isDepartmentLevel: boolean,
): any => {
  const { userInfo } = user ?? {};
  const { department } = userInfo ?? {};

  const deptFilter = {
    programmeChoice: {
      department: { eq: department },
    },
  };
  const filter = {
    and: [
      {
        status: {
          in: [
            // [RPG-1821] In certain instances, DoRPgS may determine that the available quotas are insufficient, leading to the direct rejection of student applications
            // Allow DoRPgS & GS to reject applications whose status is “submitted”.
            APPLICATION_STATUS_TYPE.SUBMITTED,

            APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR,
            APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL,

            APPLICATION_STATUS_TYPE.UNDER_DORPGS,
            APPLICATION_STATUS_TYPE.UNDER_DSRC,
            APPLICATION_STATUS_TYPE.UNDER_HOD_DOS,
            APPLICATION_STATUS_TYPE.UNDER_FRCSB,
            APPLICATION_STATUS_TYPE.UNDER_PANEL,
            APPLICATION_STATUS_TYPE.UNDER_INTERVIEW_PANEL,
          ],
        },
      },
      ...(isDepartmentLevel ? [deptFilter] : []),
    ],
  };
  return filter;
};
export const declineSupervisorFilter = (
  user: User | null,
  isDepartmentLevel: boolean,
): any => {
  const { userInfo } = user ?? {};
  const { department } = userInfo ?? {};
  const deptFilter = {
    programmeChoice: {
      department: { eq: department },
    },
  };
  const filter = {
    and: [
      {
        status: {
          // If rejected, it will clear all the unexpected data of the application
          nin: [
            // TODO tbc
            APPLICATION_STATUS_TYPE.CREATED,
            APPLICATION_STATUS_TYPE.APPLICATION_FEE_PENDING,
            APPLICATION_STATUS_TYPE.SUBMITTED,
            APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL,

            APPLICATION_STATUS_TYPE.OFFERED,
            APPLICATION_STATUS_TYPE.UNSUCCESSFUL,
            APPLICATION_STATUS_TYPE.OFFER_PROVISIONING,
            APPLICATION_STATUS_TYPE.FAILED_PROVISIONING,

            APPLICATION_STATUS_TYPE.REGISTERED,
            APPLICATION_STATUS_TYPE.REGISTRATION_FEE_PENDING,
            APPLICATION_STATUS_TYPE.REGISTRATION_FEE_SETTLED,
            APPLICATION_STATUS_TYPE.UNDER_REGISTRATION,

            APPLICATION_STATUS_TYPE.RETURNED,
            APPLICATION_STATUS_TYPE.WITHDRAWN,
            APPLICATION_STATUS_TYPE.OVERDUE,
            APPLICATION_STATUS_TYPE.DECLINED,
            APPLICATION_STATUS_TYPE.LAPSED,
            APPLICATION_STATUS_TYPE.CONDITION_MEET_LAPSED,
          ],
        },
      },
      ...(isDepartmentLevel ? [deptFilter] : []),
    ],
  };
  return filter;
};
